import { ChevronsUpDownIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/atoms/avatar";
import { Button } from "@/components/ui/atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/atoms/dropdown-menu";
import { cn } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

export type UserNavSize = "sm" | "lg";

export type UserNavProps = {
  /** Display name. @default "User" */
  name?: string;
  /** Email shown as default secondary content. */
  email?: string;
  /** Avatar image URL. Falls back to initials when omitted. */
  avatarUrl?: string;
  /** Avatar size. `"sm"` = 36px, `"lg"` = 40px. @default "sm" */
  avatarSize?: UserNavSize;
  /** Additional class names for the avatar fallback. */
  avatarClassName?: string;
  /** Badge overlay on the avatar (e.g. a notification dot). Positioned top-right automatically. */
  badge?: ReactNode;
  /** Optional badge content rendered in the dropdown identity instead of reusing the trigger badge. */
  menuBadge?: ReactNode;
  /**
   * Custom content rendered below the name.
   * Defaults to the email when omitted.
   * Pass `null` explicitly to render nothing below the name.
   */
  secondary?: ReactNode;
  /** Optional secondary content rendered in the dropdown identity instead of reusing the trigger secondary content. */
  menuSecondary?: ReactNode;
  /** Compact mode hides trigger details and chevron while keeping the dropdown identity expanded. */
  compact?: boolean;
  /**
   * Dropdown menu items (e.g. `DropdownMenuItem`, `DropdownMenuSeparator`).
   * When provided the trigger becomes interactive and opens a dropdown.
   * When omitted the component renders as a static display.
   */
  children?: ReactNode;
  /** Which side of the trigger the dropdown opens on. @default "top" */
  dropdownSide?: ComponentProps<typeof DropdownMenuContent>["side"];
  /** How the dropdown aligns with the trigger. @default "end" */
  dropdownAlign?: ComponentProps<typeof DropdownMenuContent>["align"];
  className?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const sizeClasses: Record<UserNavSize, string> = {
  sm: "size-9",
  lg: "size-10",
};

// ─── UserNavIdentity ──────────────────────────────────────────────────────────

type UserNavIdentityProps = {
  name: string;
  avatarUrl?: string;
  avatarSize: UserNavSize;
  avatarClassName?: string;
  badge?: ReactNode;
  secondaryContent: ReactNode;
  hideDetails?: boolean;
};

function UserNavIdentity({
  name,
  avatarUrl,
  avatarSize,
  avatarClassName,
  badge,
  secondaryContent,
  hideDetails = false,
}: UserNavIdentityProps) {
  const initials = getInitials(name) || "U";

  return (
    <>
      <div className="relative shrink-0">
        <Avatar
          className={cn(sizeClasses[avatarSize], "rounded-lg after:rounded-lg")}
        >
          {avatarUrl && <AvatarImage alt={name} src={avatarUrl} />}
          <AvatarFallback
            className={cn(
              "rounded-lg bg-purple-600 font-semibold text-white text-xs",
              avatarClassName
            )}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        {badge}
      </div>
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col items-start gap-px text-left leading-tight",
          hideDetails && "hidden"
        )}
      >
        <span className="w-full truncate font-semibold text-foreground text-sm">
          {name}
        </span>
        {secondaryContent}
      </div>
    </>
  );
}

// ─── UserNav ─────────────────────────────────────────────────────────────────

/**
 * Presentational user widget — avatar, name, and secondary info.
 *
 * - Without `children` it renders as a static display.
 * - With `children` (typically `DropdownMenuItem`) it renders as a dropdown.
 * - `secondary` defaults to the email; override with any ReactNode.
 *   Pass `null` explicitly to suppress the email entirely.
 *
 * @example Chat sidebar
 * ```tsx
 * <UserNav name={name} email={email}>
 *   <DropdownMenuItem onClick={onSignOut}>Sign out</DropdownMenuItem>
 * </UserNav>
 * ```
 *
 * @example Dashboard with plan badge
 * ```tsx
 * <UserNav name={name} avatarSize="lg" secondary={<PlanBadge plan="pro" />}>
 *   <DropdownMenuItem onClick={onBilling}>Billing</DropdownMenuItem>
 * </UserNav>
 * ```
 */
function UserNav({
  name,
  email,
  avatarUrl,
  avatarSize = "sm",
  avatarClassName,
  badge,
  menuBadge,
  secondary,
  menuSecondary,
  compact = false,
  children,
  dropdownSide = "top",
  dropdownAlign = "end",
  className,
}: UserNavProps) {
  const displayName = name ?? "User";

  let secondaryContent: ReactNode = null;
  // `!== undefined` intentionally allows `null` to suppress the email.
  if (secondary !== undefined) {
    secondaryContent = secondary;
  } else if (email) {
    secondaryContent = (
      <span className="w-full truncate text-muted-foreground text-xs">
        {email}
      </span>
    );
  }

  const menuSecondaryContent =
    menuSecondary === undefined ? secondaryContent : menuSecondary;

  if (!children) {
    return (
      <div
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-1.5 py-2",
          className
        )}
      >
        <UserNavIdentity
          avatarClassName={avatarClassName}
          avatarSize={avatarSize}
          avatarUrl={avatarUrl}
          badge={badge}
          hideDetails={compact}
          name={displayName}
          secondaryContent={secondaryContent}
        />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            aria-label={`${displayName} menu`}
            className={cn(
              "h-auto w-full cursor-pointer justify-start gap-3 overflow-visible rounded-lg px-1.5 py-2 transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
              "data-open:bg-accent data-open:text-accent-foreground",
              compact && "size-10 justify-center px-0",
              className
            )}
            variant="ghost"
          />
        }
      >
        <UserNavIdentity
          avatarClassName={avatarClassName}
          avatarSize={avatarSize}
          avatarUrl={avatarUrl}
          badge={badge}
          hideDetails={compact}
          name={displayName}
          secondaryContent={secondaryContent}
        />
        {!compact && (
          <ChevronsUpDownIcon className="ml-auto size-4 shrink-0 text-muted-foreground" />
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={dropdownAlign}
        className="w-(--anchor-width) min-w-56"
        side={dropdownSide}
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-3 px-3 py-2.5">
            <UserNavIdentity
              avatarClassName={avatarClassName}
              avatarSize={avatarSize}
              avatarUrl={avatarUrl}
              badge={menuBadge ?? badge}
              name={displayName}
              secondaryContent={menuSecondaryContent}
            />
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { UserNav };
